import {
  index,
  integer,
  pgEnum,
  pgTable,
  text,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

import {
  applicationTypeEnum,
  applicationTypeEnumSchema,
} from "./applicationsSchema";
import { usStatesEnum, usStatesEnumSchema } from "./commonSchema";
import { customTimestamp } from "./utils/types";

export const federalGroupEnumSchema = z.enum([
  "CHILD_0_1",
  "CHILD_1_5",
  "CHILD_6_18",
  "CHILD_1_18",
  "CHILD_0_18_CHIP",
  "ADULT_19_20",
  "PREGNANT_WOMEN",
  "PARENT_CARETAKER",
  "EXPANSION_ADULTS_19_64",
  "BREAST_CANCER",
  "CERVICAL_CANCER",
  "AGE_SINGLE",
  "BLIND_SINGLE",
  "DISABLED_SINGLE",
  "AGE_MARRIED",
  "BLIND_MARRIED",
  "DISABLED_MARRIED",
  "FORMERLY_FOSTER_CARE",
  "DISABLED",
  "AGE",
  "NO_EMPLOYER_COVERAGE",
]);
export type FederalGroupEnumType = z.infer<typeof federalGroupEnumSchema>;
export const federalGroupEnum = pgEnum(
  "federal_group_enum",
  federalGroupEnumSchema.enum,
);

// Note: When fplPercentage is null, it means the program will always be an option
// to the user regardless of what FPL their household income achieved
export const eligibilityCategoriesTable = pgTable(
  "eligibility_categories",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    usState: usStatesEnum("us_state").notNull(),
    federalGroup: federalGroupEnum("federal_group").notNull(),
    customUsStateName: text("custom_us_state_name"),
    applicationType: applicationTypeEnum("application_type").notNull(),
    fplPercentage: integer("fpl_percentage"),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    index("eligibility_categories_us_state_fpl_percentage_idx").on(
      table.usState,
      table.fplPercentage,
    ),
    uniqueIndex(
      "eligibility_categories_us_state_federal_group_application_type_idx",
    ).on(table.usState, table.federalGroup, table.applicationType),
  ],
);

export const eligibilityCategorySchema = createSelectSchema(
  eligibilityCategoriesTable,
  {
    usState: usStatesEnumSchema,
    federalGroup: federalGroupEnumSchema,
    applicationType: applicationTypeEnumSchema,
  },
);

export type EligibilityCategoryType = z.infer<typeof eligibilityCategorySchema>;

export const insertEligibilityCategoriesSchema = createInsertSchema(
  eligibilityCategoriesTable,
  {
    usState: usStatesEnumSchema,
    federalGroup: federalGroupEnumSchema,
    customUsStateName: z.string().optional().nullish(),
    applicationType: applicationTypeEnumSchema,
    fplPercentage: z.number().int().positive().nullable(),
  },
).omit({
  createdAt: true,
  updatedAt: true,
});

export type InsertEligibilityCategoriesType = z.infer<
  typeof insertEligibilityCategoriesSchema
>;
